/**
 * @file 运行时 / 会话的 React 上下文
 * 组件只能通过 useRuntime() 拿 client，通过 useAuth() 拿会话能力（规范 4.4）。
 */

import { createContext, useCallback, useContext, useSyncExternalStore, type ReactNode } from 'react';
import type { AppRuntime } from './runtime';
import type { AuthState, AuthStore } from './store';

const RuntimeContext = createContext<AppRuntime | null>(null);

/**
 * @what 注入运行时
 * @param props.runtime 由 createAppRuntime 创建
 * @param props.children 子树
 * @return JSX
 */
export function RuntimeProvider({ runtime, children }: { runtime: AppRuntime; children: ReactNode }) {
  return <RuntimeContext.Provider value={runtime}>{children}</RuntimeContext.Provider>;
}

/**
 * @what 取运行时（含 api-client）
 * @return AppRuntime
 * @throws {Error} 未包在 RuntimeProvider 内时抛出，避免静默拿到 undefined
 */
export function useRuntime(): AppRuntime {
  const runtime = useContext(RuntimeContext);
  if (!runtime) throw new Error('useRuntime 必须在 RuntimeProvider 内使用');
  return runtime;
}

/**
 * @what 取会话能力
 * @return AuthStore
 */
export function useAuth(): AuthStore {
  return useRuntime().auth;
}

/**
 * @what 订阅会话快照（useSyncExternalStore，保证并发渲染下读到一致状态）
 * @return AuthState
 */
export function useAuthState(): AuthState {
  const auth = useAuth();
  const subscribe = useCallback((listener: () => void) => auth.subscribe(listener), [auth]);
  const getSnapshot = useCallback(() => auth.getState(), [auth]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
